import React, { Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import ErrorBoundary from "@/components/error/ErrorBoundary";
import { NavigationProvider } from "@/components/shared/UnifiedNavigationTerminology";
import { AccessibilityProvider } from "@/components/shared/MobileResponsiveLayout";
import { DashboardProvider } from '@/contexts/DashboardContext';
import { LearningPathProvider } from '@/hooks/useLearningPathContext';

// Import core pages
import Index from "./pages/Index";
import Login from "./pages/auth/Login";
import Signup from "./pages/auth/Signup";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import NotFound from "./pages/NotFound";
import { StudentDashboard } from "./components/student/dashboard/StudentDashboard";

// Import discipline pages
import Convergence from "./pages/Convergence";
import HumanInquiry from "./pages/HumanInquiry";
import WorldItself from "./pages/WorldItself";
import Engineering from "./pages/Engineering";
import HealthSciences from "./pages/HealthSciences";
import Mathematics from "./pages/Mathematics";
import NaturalSciences from "./pages/NaturalSciences";
import StudyPage from "./pages/StudyPage";
import SearchPage from "./pages/SearchPage";
import Settings from "./pages/Settings";
import Profile from "./pages/Profile";
import Admin from "./pages/Admin";

// Import content pages
import ContentHub from "./pages/content/ContentHub";
import CreateContent from "./pages/content/CreateContent";
import ContentManagement from "./pages/content/ContentManagement";
import ContributionWorkflow from "./pages/content/ContributionWorkflow";

// Import dashboard pages
import AuthorDashboard from "./pages/dashboard/AuthorDashboard";
import ProgressDashboard from "./pages/dashboard/ProgressDashboard";

// Simple query client configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <AuthProvider>
            <DashboardProvider>
              <AccessibilityProvider>
                <NavigationProvider>
                  <LearningPathProvider>
                    <Toaster />
                  <BrowserRouter>
                    <Suspense fallback={
                      <div className="min-h-screen flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
                      </div>
                    }>
                      <Routes>
                        {/* Core routes */}
                        <Route path="/" element={<Index />} />
                        <Route path="/auth/login" element={<Login />} />
                        <Route path="/auth/signup" element={<Signup />} />
                        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
                        <Route path="/auth/reset-password" element={<ResetPassword />} />

                        {/* Main discipline pillars */}
                        <Route path="/human-inquiry" element={<HumanInquiry />} />
                        <Route path="/world-itself" element={<WorldItself />} />
                        <Route path="/convergence" element={<Convergence />} />

                        {/* Discipline pages */}
                        <Route path="/engineering" element={<Engineering />} />
                        <Route path="/health-sciences" element={<HealthSciences />} />
                        <Route path="/mathematics" element={<Mathematics />} />
                        <Route path="/natural-sciences" element={<NaturalSciences />} />

                        {/* Dynamic study pages */}
                        <Route path="/study/:slug" element={<StudyPage />} />
                        
                        {/* Philosophy and Theology sub-discipline routes */}
                        <Route path="/philosophy/:slug" element={<StudyPage />} />
                        <Route path="/theology/:slug" element={<StudyPage />} />
                        <Route path="/human-inquiry/philosophy/:slug" element={<StudyPage />} />
                        <Route path="/human-inquiry/theology/:slug" element={<StudyPage />} />
                        
                        {/* Utility pages */}
                        <Route path="/search" element={<SearchPage />} />
                        <Route path="/themes" element={<SearchPage />} />
                        <Route path="/library" element={<SearchPage />} />

                        {/* Content management routes */}
                        <Route path="/content" element={
                          <ProtectedRoute requireAuthentication={true}>
                            <ContentHub />
                          </ProtectedRoute>
                        } />
                        <Route path="/content/create" element={
                          <ProtectedRoute requireAuthentication={true}>
                            <CreateContent />
                          </ProtectedRoute>
                        } />
                        <Route path="/editorial" element={
                          <ProtectedRoute requireAuthentication={true}>
                            <ContentManagement />
                          </ProtectedRoute>
                        } />
                        <Route path="/contribute" element={
                          <ProtectedRoute requireAuthentication={true}>
                            <ContributionWorkflow />
                          </ProtectedRoute>
                        } />

                        {/* Dashboard routes */}
                        <Route path="/dashboard" element={
                          <ProtectedRoute requireAuthentication={true}>
                            <StudentDashboard />
                          </ProtectedRoute>
                        } />
                        <Route path="/dashboard/author" element={
                          <ProtectedRoute requireAuthentication={true}>
                            <AuthorDashboard />
                          </ProtectedRoute>
                        } />
                        <Route path="/dashboard/progress" element={
                          <ProtectedRoute requireAuthentication={true}>
                            <ProgressDashboard />
                          </ProtectedRoute>
                        } />
                        <Route path="/progress" element={
                          <ProtectedRoute requireAuthentication={true}>
                            <ProgressDashboard />
                          </ProtectedRoute>
                        } />

                        {/* User settings and profile */}
                        <Route path="/settings" element={
                          <ProtectedRoute requireAuthentication={true}>
                            <Settings />
                          </ProtectedRoute>
                        } />
                        <Route path="/profile" element={
                          <ProtectedRoute requireAuthentication={true}>
                            <Profile />
                          </ProtectedRoute>
                        } />

                        {/* Admin routes */}
                        <Route path="/admin" element={
                          <ProtectedRoute requireAuthentication={true}>
                            <Admin />
                          </ProtectedRoute>
                        } />

                        {/* 404 route */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </BrowserRouter>
                  </LearningPathProvider>
                </NavigationProvider>
              </AccessibilityProvider>
            </DashboardProvider>
          </AuthProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
